import { useState } from 'react';
import { useAuth } from '../../../../hooks/useAuth';
import useGetSpaceMembers from '../../api/useGetSpaceMembers';
import { ROLE_LABELS } from '../../constants/roles';
import type { SpaceMemberWithProfile } from '../../types';

export type MemberRowData = {
  member: SpaceMemberWithProfile;
  name: string;
  initials: string;
  email: string | null;
  role: string;
  isCurrentUser: boolean;
};

type Props = {
  spaceId: string;
};

export function getMemberName(member: SpaceMemberWithProfile) {
  return [member.profiles?.first_name, member.profiles?.last_name].filter(Boolean).join(' ');
}

function buildInitials(name: string) {
  const words = name.split(' ').filter(Boolean);
  return words.slice(0, 2).map((word) => word[0].toUpperCase()).join('') || '?';
}

function toMemberRow(member: SpaceMemberWithProfile, currentUserId: string | undefined): MemberRowData {
  const email = member.user_basic_info?.email || null;
  const name = getMemberName(member) || email || 'Membre sans nom';

  return {
    member,
    name,
    initials: buildInitials(getMemberName(member) || email || ''),
    email,
    role: ROLE_LABELS[member.role],
    isCurrentUser: member.user_id === currentUserId,
  };
}

export default function useMembersCard(props: Props) {
  const { user } = useAuth();
  const membersQuery = useGetSpaceMembers(props.spaceId);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);

  const rows = (membersQuery.data ?? []).map((member) => toMemberRow(member, user?.id));

  function handleOpenAddMember() {
    setIsAddMemberOpen(true);
  }

  function handleCloseAddMember() {
    setIsAddMemberOpen(false);
  }

  return {
    rows,
    isLoading: membersQuery.isLoading,
    isError: membersQuery.isError,
    isAddMemberOpen,
    handleOpenAddMember,
    handleCloseAddMember,
  };
}
